import { useEffect, useRef, useState } from 'react'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'
import { getBigAssetPath } from '@aytm/lib/common/assets'
import '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/ModelViewer.ts'
import {
  ASSETS_TYPES,
  assetsCollectionElement,
  IEcommerceAnswerSchema,
  IEcommerceVideoAsset,
  IFileAsset
} from '../../types.ts'
import { assetDialogEvent } from '../../utils/assetDialogEvent.ts'
import { getAssetType } from '../../utils/getAssetType.ts'
import AssetsGate from '../../components/AssetsGate.tsx'
import Spinner from './Spinner.tsx'
import AssetThumb from './AssetThumb.tsx'
import style from './AssetsModal.module.scss'

// One step: the magnifier toggles between the fitted image and a 2x view.
const ZOOM_MAX = 2

interface IAssetsModalProps {
  answer: IEcommerceAnswerSchema
  assetsCollection: assetsCollectionElement[]
  index: number
  onClose: () => void
}

const AssetsModal = ({ answer, assetsCollection, index, onClose }: IAssetsModalProps) => {
  const { t } = useSurveyLocale()
  const timeline = useTimeline()
  const viewport = useViewport()

  const [selectedIndex, setSelectedIndex] = useState<number>(index)
  const [zoom, setZoom] = useState<number>(1)
  // The zoomed photo follows the cursor: its scale origin is the pointer's spot on the photo. Written
  // straight to the element, since it changes on every mouse move and nothing else reads it.
  const imageRef = useRef<HTMLDivElement>(null)

  const playingIndexRef = useRef<number | null>(null)
  const zoomedIndexRef = useRef<number | null>(null)

  const currentAsset = assetsCollection[selectedIndex]
  const currentType = getAssetType(currentAsset)

  const logVideoEvent = (type: 'play_video' | 'pause_video', assetIndex: number) =>
    timeline.log({ type, ref: answer.id, target: assetIndex.toString(), context: ASSETS_TYPES.VIDEO.toLowerCase() })

  // The <video> unmounts on asset switch and on close, so its own pause event never reaches React.
  const flushPlayingVideo = () => {
    if (null === playingIndexRef.current) return
    logVideoEvent('pause_video', playingIndexRef.current)
    playingIndexRef.current = null
  }

  // Asset switch and close reset the zoom without a minus click; log the matching zoom-out once.
  const flushZoom = () => {
    if (null === zoomedIndexRef.current) return
    timeline.log({ type: 'zoom_image_out', ref: answer.id, target: zoomedIndexRef.current.toString() })
    zoomedIndexRef.current = null
  }

  const onZoomIn = () => {
    if (zoom >= ZOOM_MAX) return
    timeline.log({ type: 'zoom_image_in', ref: answer.id, target: selectedIndex.toString() })
    zoomedIndexRef.current = selectedIndex
    setZoom(ZOOM_MAX)
  }

  const onZoomOut = () => {
    if (1 === zoom) return
    timeline.log({ type: 'zoom_image_out', ref: answer.id, target: selectedIndex.toString() })
    zoomedIndexRef.current = null
    setZoom(1)
    if (imageRef.current) imageRef.current.style.transformOrigin = ''
  }

  const followPointer = (point: { clientX: number; clientY: number }, viewport: HTMLElement) => {
    const image = imageRef.current
    if (!image) return
    const box = viewport.getBoundingClientRect()
    const x = Math.max(0, Math.min(100, ((point.clientX - box.left) / box.width) * 100))
    const y = Math.max(0, Math.min(100, ((point.clientY - box.top) / box.height) * 100))
    image.style.transformOrigin = `${x}% ${y}%`
  }

  const onAssetSelect = (assetIndex: number) => {
    if (assetIndex === selectedIndex) return // same-thumb click: nothing changes, a flush here would orphan the playing video
    flushPlayingVideo()
    flushZoom()
    // Crossing a type boundary re-frames the dialog: close the outgoing type, open the incoming one.
    const next = assetsCollection[assetIndex]
    if (getAssetType(currentAsset) !== getAssetType(next)) {
      timeline.log({ ref: answer.id, type: assetDialogEvent('close', currentAsset) })
      timeline.log({ ref: answer.id, type: assetDialogEvent('open', next) })
    }
    setSelectedIndex(assetIndex)
    setZoom(1)
    if (imageRef.current) imageRef.current.style.transformOrigin = ''
  }

  const closeModal = () => {
    flushPlayingVideo()
    flushZoom()
    timeline.log({ ref: answer.id, type: assetDialogEvent('close', currentAsset) })
    onClose()
  }

  useEffect(() => {
    timeline.log({
      type: 'show_asset',
      ref: answer.id,
      target: selectedIndex.toString(),
      context: (getAssetType(assetsCollection[selectedIndex]) ?? '').toLowerCase()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex])

  // No deps: closeModal closes over the current asset, so the handler must be re-bound each render.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ('Escape' === event.key) closeModal()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  })

  useEffect(
    () => () => {
      flushPlayingVideo()
      flushZoom()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <div
      className={`${style.overlay} ${viewport.mobile ? style.overlayMobile : ''}`}
      onClick={closeModal}
      data-testid="SW-EC-AssetsModal"
    >
      <div className={style.dialog} onClick={event => event.stopPropagation()}>
        <div className={style.header}>
          <div className={style.title} dangerouslySetInnerHTML={{ __html: answer.text }} />
          <button
            type="button"
            className={style.close}
            onClick={closeModal}
            aria-label={t('questions', 'ec', 'tesco', 'close')}
          >
            <i className="pi pi-times" />
          </button>
        </div>

        <div className={style.body}>
          <div className={style.stage}>
            {/* A photo waits behind the Tesco spinner; the video tag and the model viewer show their own loaders. */}
            {ASSETS_TYPES.IMAGE === currentType && (
              <AssetsGate urls={[getBigAssetPath(currentAsset as IImageAsset)]} loader={<Spinner />}>
                <div
                  className={style.imageViewport}
                  onMouseMove={event => followPointer(event, event.currentTarget)}
                  onTouchMove={event => followPointer(event.touches[0], event.currentTarget)}
                  onClick={() => (1 === zoom ? onZoomIn() : onZoomOut())}
                >
                  <div
                    ref={imageRef}
                    className={style.image}
                    role="img"
                    aria-label={(currentAsset as IImageAsset).alt_text || t('questions', 'img', 'noAlt')}
                    style={{
                      backgroundImage: `url(${getBigAssetPath(currentAsset as IImageAsset)})`,
                      transform: `scale(${zoom})`,
                      cursor: zoom > 1 ? 'zoom-out' : 'zoom-in',
                      transition: 'transform 0.2s ease-out'
                    }}
                  />
                </div>
              </AssetsGate>
            )}
            {ASSETS_TYPES.VIDEO === currentType && (
              <video
                key={selectedIndex} // remount on video->video switch; a reused node can fire the browser's src-change pause into the new index
                playsInline
                controls
                className={style.video}
                src={(currentAsset as IEcommerceVideoAsset).video.url}
                preload="metadata"
                onPlay={() => {
                  playingIndexRef.current = selectedIndex
                  logVideoEvent('play_video', selectedIndex)
                }}
                onPause={() => {
                  playingIndexRef.current = null
                  logVideoEvent('pause_video', selectedIndex)
                }}
              />
            )}
            {ASSETS_TYPES.MODEL === currentType && (
              <div className={style.model}>
                <model-viewer
                  src={(currentAsset as IFileAsset).file_url}
                  camera-controls
                  touch-action="pan-y"
                  interaction-prompt="none"
                />
              </div>
            )}
          </div>
          <button
            type="button"
            className={`${style.control} ${style.prev}`}
            disabled={0 === selectedIndex}
            onClick={() => onAssetSelect(selectedIndex - 1)}
            aria-label={t('questions', 'ec', 'tesco', 'previous')}
          >
            <i className="pi pi-chevron-left" />
          </button>
          <button
            type="button"
            className={`${style.control} ${style.next}`}
            disabled={assetsCollection.length - 1 === selectedIndex}
            onClick={() => onAssetSelect(selectedIndex + 1)}
            aria-label={t('questions', 'ec', 'tesco', 'next')}
          >
            <i className="pi pi-chevron-right" />
          </button>

          {ASSETS_TYPES.IMAGE === currentType && (
            <div className={style.zoomControls}>
              <button
                type="button"
                className={style.control}
                disabled={zoom >= ZOOM_MAX}
                onClick={onZoomIn}
                aria-label={t('questions', 'ec', 'tesco', 'zoomIn')}
              >
                <i className="pi pi-search-plus" />
              </button>
              <button
                type="button"
                className={style.control}
                disabled={1 === zoom}
                onClick={onZoomOut}
                aria-label={t('questions', 'ec', 'tesco', 'zoomOut')}
              >
                <i className="pi pi-search-minus" />
              </button>
            </div>
          )}
        </div>

        {assetsCollection.length > 1 && (
          <div className={style.thumbs}>
            {assetsCollection.map((asset, assetIndex) => (
              <AssetThumb
                key={assetIndex}
                asset={asset}
                active={assetIndex === selectedIndex}
                onClick={() => onAssetSelect(assetIndex)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default AssetsModal
